import React from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import "./FNOLForm.css";
import DocumentViewer from "./DocumentViewer";

import OriginalEvidence from "../../components/OriginalEvidence";
import SystemGeneratedInfo from "../../components/SystemGeneratedInfo";

/* ---------- Helpers ---------- */
const Req = () => <span className="text-danger ms-1">*</span>;

function formatCurrencyAmount(value) {
    if (!value) return "";
    const num = parseFloat(value);
    if (isNaN(num)) return value;

    // Format with commas (e.g. 1,000,000)
    return num.toLocaleString('en-US');
}


export default function FNOLForm({
    form,
    setF,
    isEditMode,
    loadingFNOL,
    isCompleted,
    handleEdit,
    handleComplete,
    onContinue,
    handleCancel,
    handleSubmit,
    hasChanges,
    revalidating,
    hasClaimId,
    // DocumentViewer Props
    documents,
    filteredDocuments,
    loadingDocuments,
    selectedDocId,
    setSelectedDocId,
    selectedDoc,
    documentSearchQuery,
    onDocumentSearchChange,
    onOpenPreview,
    inlinePreviewUrl,
    inlinePreviewLoading,
    setIsSplitScreen,
    isSplitScreen,
    inboxOpen,
    setInboxOpen,
    originalEvidence,
    fnolId,
}) {
    const [comment, setComment] = React.useState('');
    const commentTextareaRef = React.useRef(null);

    const [subLimitDropdownOpen, setSubLimitDropdownOpen] = React.useState(false);
    const subLimitDropdownRef = React.useRef(null);

    React.useEffect(() => {
        function handleClickOutside(event) {
            if (subLimitDropdownRef.current && !subLimitDropdownRef.current.contains(event.target)) {
                setSubLimitDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const toggleSubLimitOption = (key) => {
        if (!isEditMode) return;
        const current = (form.subLimitType && typeof form.subLimitType === 'object') ? form.subLimitType : {};
        const currentVal = current[key];
        // If value is truthy (selected), set it to empty string (deselect)
        // If value is falsy (deselected), restore it to "true" or its non-empty previous value
        const isCurrentlySelected = currentVal !== undefined && currentVal !== '' && currentVal !== '0' && currentVal !== false;
        setF("subLimitType", { ...current, [key]: isCurrentlySelected ? '' : (currentVal || 'true') });
    };

    // Comment formatting functions
    const applyFormatting = (prefix, suffix = '') => {
        const textarea = commentTextareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = comment.substring(start, end);
        const beforeText = comment.substring(0, start);
        const afterText = comment.substring(end);

        const newText = beforeText + prefix + selectedText + suffix + afterText;
        setComment(newText);

        setTimeout(() => {
            textarea.focus();
            const newCursorPos = start + prefix.length + selectedText.length + suffix.length;
            textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
    };

    const handleBold = () => applyFormatting('**', '**');
    const handleItalic = () => applyFormatting('*', '*');
    const handleUnderline = () => applyFormatting('<u>', '</u>');
    const handleStrikethrough = () => applyFormatting('~~', '~~');
    const handleBulletList = () => {
        const textarea = commentTextareaRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const beforeText = comment.substring(0, start);
        const afterText = comment.substring(start);
        const newText = beforeText + '\n• ';
        setComment(newText + afterText);
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(newText.length, newText.length);
        }, 0);
    };
    const handleNumberedList = () => {
        const textarea = commentTextareaRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const beforeText = comment.substring(0, start);
        const afterText = comment.substring(start);
        const newText = beforeText + '\n1. ';
        setComment(newText + afterText);
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(newText.length, newText.length);
        }, 0);
    };
    const isFormValid = (
        form?.lossName &&
        form?.dateOfLoss &&
        form?.timeOfLoss &&
        form?.cause &&
        form?.description &&
        form?.currency &&
        form?.amount &&
        form?.location &&
        form?.reportedDate &&
        form?.policyNumber &&
        form?.insuredName &&
        form?.inceptionDate &&
        form?.expirationDate &&
        form?.policyStatus &&
        form?.lineOfBusiness
    );

    return (
        <>
            {/* Loss Details */}
            <section className="card fnol-card mb-2 px-2">
                <div className="card-body">
                    <h5 className="card-title fnol-card-title">Loss Details</h5>
                    <div className="ps-2">
                        <div className="mb-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <label className="form-label fnol-label mb-1">Loss Name <Req /></label>
                                <SystemGeneratedInfo label="Loss Name" infoText="Loss Name is auto generated using Insured Name from policy records and Outlook submission timestamp." />
                            </div>
                            <input
                                className="form-control form-control-sm fnol-input"
                                placeholder="Enter Loss Name"
                                value={form.lossName}
                                onChange={(e) => setF("lossName", e.target.value)}
                                disabled={!isEditMode}
                            />
                        </div>

                        <div className="mb-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <label className="form-label fnol-label mb-1">Claimant</label>
                                <OriginalEvidence value={originalEvidence?.claimant_name} label="Claimant Name" fnolId={fnolId} fieldKey="claimant_name" hasClaimId={hasClaimId} />
                            </div>
                            <input
                                className="form-control form-control-sm fnol-input"
                                placeholder="Enter Claimant Name"
                                value={form.claimant}
                                onChange={(e) => setF("claimant", e.target.value)}
                                disabled={!isEditMode}
                            />
                        </div>

                        <div className="row g-2 mb-2">
                            <div className="col-12 col-md-6">
                                <div className="d-flex justify-content-between align-items-center">
                                    <label className="form-label fnol-label mb-1">Date of Loss <Req /></label>
                                    <OriginalEvidence value={originalEvidence?.date_of_loss} label="Date of Loss" fnolId={fnolId} fieldKey="date_of_loss" hasClaimId={hasClaimId} />
                                </div>
                                <input
                                    type="date"
                                    className="form-control form-control-sm fnol-input"
                                    value={form.dateOfLoss}
                                    onChange={(e) => setF("dateOfLoss", e.target.value)}
                                    disabled={!isEditMode}
                                />
                            </div>
                            <div className="col-12 col-md-6">
                                <div className="d-flex justify-content-between align-items-center">
                                    <label className="form-label fnol-label mb-1">Time of Loss <Req /></label>
                                    <OriginalEvidence value={originalEvidence?.time_of_loss} label="Time of Loss" fnolId={fnolId} fieldKey="time_of_loss" hasClaimId={hasClaimId} />
                                </div>
                                <input
                                    type="time"
                                    className="form-control form-control-sm fnol-input"
                                    value={form.timeOfLoss}
                                    onChange={(e) => setF("timeOfLoss", e.target.value)}
                                    disabled={!isEditMode}
                                />
                            </div>
                        </div>

                        <div className="mb-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <label className="form-label fnol-label mb-1">Cause of Loss <Req /></label>
                                <OriginalEvidence value={originalEvidence?.cause_of_loss} label="Cause of Loss" fnolId={fnolId} fieldKey="cause_of_loss" hasClaimId={hasClaimId} />
                            </div>
                            <select
                                className="form-select form-select-sm fnol-select"
                                value={form.cause}
                                onChange={(e) => setF("cause", e.target.value)}
                                disabled={!isEditMode}
                            >
                                <option value="Electrical Short Circuit">Electrical Short Circuit</option>
                                <option value="Fire">Fire</option>
                                <option value="Lightning">Lightning</option>
                                <option value="Explosion">Explosion</option>
                                <option value="Aircraft Damage">Aircraft Damage</option>
                                <option value="Flood">Flood</option>
                                <option value="Storm">Storm</option>
                                <option value="Cyclone">Cyclone</option>
                                <option value="Typhoon">Typhoon</option>
                                <option value="Hurricane">Hurricane</option>
                                <option value="Tornado">Tornado</option>
                                <option value="Heavy Rainfall">Heavy Rainfall</option>
                                <option value="Earthquake">Earthquake</option>
                                <option value="Tsunami">Tsunami</option>
                                <option value="Volcanic Eruption">Volcanic Eruption</option>
                                <option value="Burst Pipe">Burst Pipe</option>
                                <option value="Roof Leakage">Roof Leakage</option>
                                <option value="Burglary (Forced Entry)">Burglary (Forced Entry)</option>
                                <option value="Housebreaking">Housebreaking</option>
                                <option value="Robbery">Robbery</option>
                                <option value="Theft (No Forced Entry)">Theft (No Forced Entry)</option>
                                <option value="Vandalism">Vandalism</option>
                                <option value="Terrorism">Terrorism</option>
                            </select>
                        </div>

                        <div className="mb-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <label className="form-label fnol-label mb-1">Loss Description <Req /></label>
                                <OriginalEvidence value={originalEvidence?.loss_description} label="Loss Description" fnolId={fnolId} fieldKey="loss_description" hasClaimId={hasClaimId} />
                            </div>
                            <textarea
                                className="form-control form-control-sm fnol-input"
                                rows={10}
                                style={{ resize: "none" }}
                                value={form.description}
                                onChange={(e) => setF("description", e.target.value)}
                                disabled={!isEditMode}
                            />
                        </div>

                        <div className="row g-2 mb-2">
                            <div className="col-12 col-md-6">
                                <div className="d-flex justify-content-between align-items-center">
                                    <label className="form-label fnol-label mb-1">Loss Currency <Req /></label>
                                    <OriginalEvidence value={originalEvidence?.estimated_loss_currency} label="Loss Currency" fnolId={fnolId} fieldKey="estimated_loss_currency" hasClaimId={hasClaimId} />
                                </div>
                                <select
                                    className="form-select form-select-sm fnol-select"
                                    value={form.currency}
                                    onChange={(e) => setF("currency", e.target.value)}
                                    disabled={!isEditMode}
                                >
                                    <option value="USD">USD ($)</option>
                                    <option value="EUR">EUR (€)</option>
                                    <option value="GBP">GBP (£)</option>
                                    <option value="JPY">JPY (¥)</option>
                                    <option value="Rupee (₹)">INR (₹)</option>
                                    <option value="Unknown">Unknown</option>
                                </select>
                            </div>
                            <div className="col-12 col-md-6">
                                <div className="d-flex justify-content-between align-items-center">
                                    <label className="form-label fnol-label mb-1">Estimated Loss Amount <Req /></label>
                                    <OriginalEvidence value={originalEvidence?.estimated_loss_amount} label="Estimated Loss Amount" fnolId={fnolId} fieldKey="estimated_loss_amount" hasClaimId={hasClaimId} />
                                </div>
                                <input
                                    className="form-control form-control-sm fnol-input"
                                    placeholder=""
                                    value={isEditMode ? form.amount : formatCurrencyAmount(form.amount)}
                                    onChange={(e) => setF("amount", e.target.value)}
                                    disabled={!isEditMode}
                                />
                            </div>
                        </div>

                        <div className="row g-2 mb-2">
                            <div className="col-12 col-md-6">
                                <div className="d-flex justify-content-between align-items-center">
                                    <label className="form-label fnol-label mb-1">Loss Location <Req /></label>
                                    <OriginalEvidence value={originalEvidence?.loss_location} label="Loss Location" fnolId={fnolId} fieldKey="loss_location" hasClaimId={hasClaimId} />
                                </div>
                                <input
                                    className="form-control form-control-sm fnol-input"
                                    placeholder="Unknown Location"
                                    value={form.location}
                                    onChange={(e) => setF("location", e.target.value)}
                                    disabled={!isEditMode}
                                />
                            </div>
                            <div className="col-12 col-md-6">
                                <div className="d-flex justify-content-between align-items-center">
                                    <label className="form-label fnol-label mb-1">GPS Coordinates</label>
                                    <OriginalEvidence value={originalEvidence?.gps_coordinates} label="GPS Coordinates" fnolId={fnolId} fieldKey="gps_coordinates" hasClaimId={hasClaimId} />
                                </div>
                                <input
                                    className="form-control form-control-sm fnol-input"
                                    placeholder="Enter Coordinates"
                                    value={form.gps}
                                    onChange={(e) => setF("gps", e.target.value)}
                                    disabled={!isEditMode}
                                />
                            </div>
                        </div>

                        <div className="row g-2 mb-2">
                            <div className="col-12 col-md-6">
                                <label className="form-label fnol-label">Reported Date <Req /></label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm fnol-input"
                                    value={form.reportedDate}
                                    onChange={(e) => setF("reportedDate", e.target.value)}
                                    disabled={!isEditMode}
                                />
                            </div>
                            <div className="col-12 col-md-6">
                                <div className="d-flex justify-content-between align-items-center">
                                    <label className="form-label fnol-label mb-1">Reported By</label>
                                    <OriginalEvidence value={originalEvidence?.reported_by} label="Reported By" fnolId={fnolId} fieldKey="reported_by" hasClaimId={hasClaimId} />
                                </div>
                                <input
                                    className="form-control form-control-sm fnol-input"
                                    value={form.reportedBy}
                                    onChange={(e) => setF("reportedBy", e.target.value)}
                                    disabled={!isEditMode}
                                />
                            </div>
                        </div>

                        <div className="mb-2" ref={subLimitDropdownRef}>
                            <div className="d-flex justify-content-between align-items-center">
                                <label className="form-label fnol-label mb-1">Sub-Limit Type <Req /></label>
                                <OriginalEvidence
                                    value={originalEvidence?.sublimit ?? originalEvidence?.sub_limit_type ?? originalEvidence?.sub_limit}
                                    label="Sub-Limit Type"
                                    fnolId={fnolId}
                                    fieldKey="sublimit"
                                    fieldAliases={['sub_limit_type', 'sub_limit']}
                                    hasClaimId={hasClaimId}
                                />
                            </div>
                            <div className="position-relative">
                                {/* Pill display box */}
                                <div
                                    className="form-control form-control-sm fnol-input d-flex flex-wrap align-items-center gap-1"
                                    style={{
                                        minHeight: "36px",
                                        cursor: isEditMode ? "pointer" : "default",
                                        backgroundColor: isEditMode ? "#fff" : "#e9ecef",
                                        padding: !isEditMode ? "4px 8px" : undefined
                                    }}
                                    onClick={() => isEditMode && setSubLimitDropdownOpen(!subLimitDropdownOpen)}
                                >
                                    {(() => {
                                        const subLimitObj = (form.subLimitType && typeof form.subLimitType === 'object') ? form.subLimitType : {};
                                        const selectedKeys = Object.keys(subLimitObj).filter(k => {
                                            const v = subLimitObj[k];
                                            return v !== '' && v !== '0' && v !== false && v !== null && v !== undefined;
                                        });
                                        if (selectedKeys.length === 0) {
                                            return !isEditMode ? null : <span className="text-muted" style={{ fontSize: "13px" }}>Select Sub-Limit Types</span>;
                                        }
                                        return selectedKeys.map(key => (
                                            <span
                                                key={key}
                                                className={`badge ${!isEditMode ? 'text-dark' : 'd-flex align-items-center'}`}
                                                style={{
                                                    backgroundColor: !isEditMode ? "#c9d0d9" : "#cbd5e1",
                                                    color: isEditMode ? "#334155" : undefined,
                                                    padding: !isEditMode ? "3px 6px" : "4px 8px",
                                                    fontSize: "12px",
                                                    fontWeight: "normal",
                                                    borderRadius: "4px"
                                                }}
                                            >
                                                {key}
                                                {isEditMode && (
                                                    <X
                                                        size={12}
                                                        className="ms-1"
                                                        style={{ cursor: "pointer", opacity: 0.7 }}
                                                        onClick={(e) => { e.stopPropagation(); toggleSubLimitOption(key); }}
                                                    />
                                                )}
                                            </span>
                                        ));
                                    })()}
                                    {isEditMode && (
                                        <div className="ms-auto d-flex flex-column align-items-center justify-content-center" style={{ width: "20px", flexShrink: 0 }}>
                                            <ChevronUp size={14} className="text-secondary" style={{ marginBottom: "-4px" }} />
                                            <ChevronDown size={14} className="text-secondary" />
                                        </div>
                                    )}
                                </div>
                                {/* Dropdown options — keys from the backend object */}
                                {subLimitDropdownOpen && isEditMode && (() => {
                                    const subLimitObj = (form.subLimitType && typeof form.subLimitType === 'object') ? form.subLimitType : {};
                                    const allKeys = Object.keys(subLimitObj);
                                    if (allKeys.length === 0) {
                                        return (
                                            <div className="position-absolute w-100 bg-white border rounded shadow-sm mt-1 px-3 py-2" style={{ zIndex: 1000, fontSize: "13px", color: "#6c757d" }}>
                                                No sub-limit types available
                                            </div>
                                        );
                                    }
                                    return (
                                        <div
                                            className="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
                                            style={{ zIndex: 1000, maxHeight: "220px", overflowY: "auto" }}
                                        >
                                            {allKeys.map(key => {
                                                const val = subLimitObj[key];
                                                const isSelected = val !== '' && val !== '0' && val !== false && val !== null && val !== undefined;
                                                return (
                                                    <div
                                                        key={key}
                                                        className="px-3 py-2 d-flex align-items-center"
                                                        style={{ cursor: "pointer", backgroundColor: isSelected ? "#f1f5f9" : "transparent", fontSize: "13px", borderBottom: "1px solid #f3f4f6" }}
                                                        onClick={() => toggleSubLimitOption(key)}
                                                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = "#f8f9fa"; }}
                                                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? "#f1f5f9" : "transparent"; }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input mb-0 me-2 "
                                                            checked={isSelected}
                                                            readOnly
                                                            style={{
                                                                accentColor: "#006BB3",
                                                                backgroundColor: isSelected ? "#006BB3" : "transparent",
                                                                borderColor: isSelected ? "#006BB3" : "#ced4da",
                                                                transform: "scale(0.85)",
                                                                marginTop: 0,
                                                                flexShrink: 0
                                                            }}
                                                        />
                                                        <span style={{ flex: 1 }}>{key}</span>
                                                        {val && val !== 'true' && (
                                                            <span className="text-muted ms-2" style={{ fontSize: "11px" }}>{val}</span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        <div className="mb-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <label className="form-label fnol-label mb-1">Global Loss Event (GLE)</label>
                                <OriginalEvidence value={originalEvidence?.global_loss_details} label="Global Loss Event" fnolId={fnolId} fieldKey="global_loss_details" hasClaimId={hasClaimId} />
                            </div>
                            <input
                                className="form-control form-control-sm fnol-input"
                                placeholder="Enter Global Loss Event"
                                value={form.globalLossEvent}
                                onChange={(e) => setF("globalLossEvent", e.target.value)}
                                disabled={!isEditMode}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Policy Details */}
            <section className="card fnol-card mb-2">
                <div className="card-body">
                    <h5 className="card-title fnol-card-title">Policy Details</h5>
                    <div className="ps-2">
                        <div className="row g-2">
                            <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center">
                                    <label className="form-label fnol-label">Policy Number <Req />
                                        {form.multiplePolicyNumber && Array.isArray(form.multiplePolicyNumber) && form.multiplePolicyNumber.length > 1 && (
                                            <span className="text-danger small ms-1">(Multiple Policy Numbers Detected)</span>
                                        )}
                                    </label>
                                    <OriginalEvidence value={originalEvidence?.policy_number} label="Policy Number" fnolId={fnolId} fieldKey="policy_number" hasClaimId={hasClaimId} />
                                </div>
                                <input
                                    type="text"
                                    className="form-control form-control-sm fnol-input"
                                    value={form.policyNumber}
                                    onChange={(e) => setF("policyNumber", e.target.value)}
                                    disabled={!isEditMode}
                                    placeholder="Enter Policy Number"
                                />
                            </div>
                            <div className="col-12">
                                <label className="form-label fnol-label">Insured Name </label>
                                <input
                                    className="form-control form-control-sm fnol-input"
                                    value={form.insuredName}
                                    readOnly
                                />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label fnol-label">Inception Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm fnol-input"
                                    value={form.inceptionDate}
                                    readOnly
                                />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label fnol-label">Expiration Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm fnol-input"
                                    value={form.expirationDate}
                                    readOnly
                                />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label fnol-label">Policy Status</label>
                                <input
                                    className="form-control form-control-sm fnol-input"
                                    value={form.policyStatus}
                                    readOnly
                                />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label fnol-label">Line of Business</label>
                                <input
                                    className="form-control form-control-sm fnol-input"
                                    value={form.lineOfBusiness}
                                    readOnly
                                />
                            </div>
                            <div className="col-12">
                                <label className="form-label fnol-label">Broker Name</label>
                                <input
                                    className="form-control form-control-sm fnol-input"
                                    value={form.broker}
                                    placeholder="Enter Broker Name"
                                    readOnly
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Mobile Document Viewer */}
            <section className="d-lg-none mb-2">
                <DocumentViewer
                    documents={documents}
                    filteredDocuments={filteredDocuments}
                    loadingDocuments={loadingDocuments}
                    selectedDocId={selectedDocId}
                    setSelectedDocId={setSelectedDocId}
                    selectedDoc={selectedDoc}
                    documentSearchQuery={documentSearchQuery}
                    onDocumentSearchChange={onDocumentSearchChange}
                    onOpenPreview={onOpenPreview}
                    inlinePreviewUrl={inlinePreviewUrl}
                    inlinePreviewLoading={inlinePreviewLoading}
                    isSplitScreen={isSplitScreen}
                    setIsSplitScreen={setIsSplitScreen}
                    inboxOpen={inboxOpen}
                    setInboxOpen={setInboxOpen}
                    hideSplitToggle={true}
                />
            </section>

            {/* Comments Section */}
            <section className="card mb-2" style={{ borderRadius: '10px' }}>
                <div className="card-body">
                    {/* Label and Toolbar Row */}
                    <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                        <label className="form-label fw-bold mb-0">
                            Comments
                        </label>
                        {/* Toolbar */}
                        <div className="d-flex gap-1 align-items-center flex-wrap">
                            <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Bold" onClick={handleBold} style={{ width: '1.75rem', height: '1.75rem' }}>
                                <strong style={{ fontSize: '0.85rem' }}>B</strong>
                            </button>
                            <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Italic" onClick={handleItalic} style={{ width: '1.75rem', height: '1.75rem' }}>
                                <em style={{ fontSize: '0.85rem' }}>I</em>
                            </button>
                            <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Underline" onClick={handleUnderline} style={{ width: '1.75rem', height: '1.75rem' }}>
                                <u style={{ fontSize: '0.85rem' }}>U</u>
                            </button>
                            <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Strikethrough" onClick={handleStrikethrough} style={{ width: '1.75rem', height: '1.75rem' }}>
                                <s style={{ fontSize: '0.85rem' }}>S</s>
                            </button>
                            <div className="vr" style={{ height: '1.25rem' }}></div>
                            <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Bullet List" onClick={handleBulletList} style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                                ≡
                            </button>
                            <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Numbered List" onClick={handleNumberedList} style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                                ≣
                            </button>
                            <div className="vr" style={{ height: '1.25rem' }}></div>
                            <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Link" style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                                🔗
                            </button>
                            <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Image" style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                                🖼
                            </button>
                            <button type="button" className="btn btn-sm btn-light border-0 p-1" title="More" style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                                ⋯
                            </button>
                        </div>
                    </div>
                    {/* Text Area */}
                    <textarea
                        ref={commentTextareaRef}
                        className="form-control"
                        rows="6"
                        placeholder=""
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        style={{
                            resize: 'none',
                            borderRadius: '6px',
                            borderColor: '#ced4da'
                        }}
                    ></textarea>
                </div>
            </section>

            {/* Action Buttons Section */}
            <section className="card" style={{ borderRadius: '10px' }}>
                <div className="card-body">
                    <div className="d-flex justify-content-end gap-2 action-buttons-container">
                        {!isEditMode ? (
                            <>
                                <button
                                    type="button"
                                    className="btn fnol-action-btn fnol-btn-outline"
                                    style={hasClaimId ? {
                                        borderColor: '#CCCCCC',
                                        color: '#CCCCCC',
                                        backgroundColor: 'transparent',
                                        cursor: 'not-allowed'
                                    } : {}}
                                    onClick={handleEdit}
                                    disabled={loadingFNOL || hasClaimId}
                                >
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    className={`btn fnol-action-btn ${isCompleted ? 'btn-success' : 'fnol-btn-primary'}`}
                                    style={{
                                        backgroundColor: !isFormValid ? '#CCCCCC' : (isCompleted ? '#006BB3' : undefined),
                                        borderColor: !isFormValid ? '#CCCCCC' : '#006BB3',
                                        color: !isFormValid ? 'white' : (isCompleted ? 'white' : undefined),
                                        cursor: isFormValid ? 'pointer' : 'not-allowed'
                                    }}
                                    onClick={handleComplete}
                                    disabled={loadingFNOL || !isFormValid || hasClaimId}
                                >
                                    {isCompleted ? (
                                        <>
                                            <span style={{ marginRight: '6px' }}>✓</span>
                                            Completed
                                        </>
                                    ) : (
                                        'Complete'
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className="btn fnol-action-btn fnol-btn-wide btn-primary"
                                    style={{
                                        borderRadius: '6px',
                                        // padding removed to use responsive CSS class
                                        fontWeight: '500',
                                        backgroundColor: isFormValid ? '#006BB3' : '#CCCCCC',
                                        borderColor: isFormValid ? '#006BB3' : '#CCCCCC',
                                        cursor: isFormValid ? 'pointer' : 'not-allowed'
                                    }}
                                    onClick={onContinue}
                                    disabled={!isFormValid}
                                >
                                    Next
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className="btn fnol-action-btn btn-primary"
                                    style={{
                                        borderRadius: '6px',
                                        // padding removed to use responsive CSS class
                                        fontWeight: '500',
                                        backgroundColor: 'white',
                                        borderColor: '#006BB3',
                                        color: '#006BB3'
                                    }}
                                    onClick={handleCancel}
                                    disabled={loadingFNOL}
                                >
                                    <i className="bi bi-x-circle me-1"></i>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn fnol-action-btn btn-primary"
                                    style={{
                                        borderRadius: '6px',
                                        // padding removed to use responsive CSS class
                                        fontWeight: '500',
                                        backgroundColor: '#006BB3',
                                        borderColor: '#006BB3'
                                    }}
                                    onClick={handleSubmit}
                                    disabled={!hasChanges || loadingFNOL || revalidating}
                                >
                                    {loadingFNOL ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                            Saving...
                                        </>
                                    ) : revalidating ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                            Re-validating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-check-circle me-1"></i>
                                            Save Changes
                                        </>
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </section>
        </>
    );
}
